var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/hooks/useAuth.ts
import { useEffect, useState } from "react";
import { debounce } from "debounce";
import { GenericError, StandaloneAuth as StandaloneAuth2 } from "@leagueplatform/auth-standalone";
import { Auth, Implementation } from "@leagueplatform/auth";

// src/checkAuth.ts
import { StandaloneAuth } from "@leagueplatform/auth-standalone";
import { attachCookiesWithAccessToken } from "@leagueplatform/auth";
import { captureError } from "@leagueplatform/observability";
import { getConfig } from "@leagueplatform/config";
function getAuth0Error() {
  const searchParams = new URLSearchParams(window.location.search);
  const errorType = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  if (errorType) {
    return { type: errorType, description: errorDescription };
  }
  return null;
}
function isRedirectFromLogin() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.has("code") && searchParams.has("state");
}
function removeAuthQueryParams() {
  const url = new URL(window.location.toString());
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState(null, "", url.toString());
}
function checkAuth() {
  return __async(this, null, function* () {
    const authenticated = yield StandaloneAuth.client.isAuthenticated();
    if (authenticated) {
      StandaloneAuth.client.getTokenSilently().then((token) => {
        attachCookiesWithAccessToken(getConfig().config.core.contentUrl, token);
      }).catch((e) => {
        captureError(
          new Error(
            `Could not create access token cookie because there was an error getting an auth token. ${e}`
          )
        );
      });
      return { isAuthenticated: true };
    }
    const authError = getAuth0Error();
    if (authError) {
      return { isAuthenticated: false, error: authError };
    }
    if (isRedirectFromLogin()) {
      const { appState } = yield StandaloneAuth.client.handleRedirectCallback();
      removeAuthQueryParams();
      const isAuthenticated = yield StandaloneAuth.client.isAuthenticated();
      if (isAuthenticated) {
        return {
          isAuthenticated: true,
          appState
        };
      }
    }
    return { isAuthenticated: false };
  });
}

// src/hooks/useAuth.ts
var CHECK_SESSION_DEBOUNCE_TIMER = 1e3;
function useAuth({
  redirectToLogInWhenSignedOut = false
} = {}) {
  const isEmbedded = Auth.implementation === Implementation.EMBEDDED;
  const [state, setState] = useState({
    /**
     * If we're in embedded mode, we ASSUME that we're authenticated. This is here only to keep the `useAuth` hook
     * usable in apps which can be initialized in both standalone and embedded modes (e.g., a standalone
     * app that is sometimes loaded in a mobile webview).
     */
    isAuthenticated: isEmbedded ? true : void 0,
    error: null
  });
  useEffect(() => {
    const checkSessionValidity = debounce(
      () => __async(this, null, function* () {
        if (!state.isAuthenticated) {
          return;
        }
        try {
          yield StandaloneAuth2.client.getTokenSilently();
        } catch (err) {
          const typedErr = err;
          setState((current) => __spreadProps(__spreadValues({}, current), {
            isAuthenticated: false,
            error: typedErr instanceof GenericError ? {
              type: typedErr.error,
              description: typedErr.error_description
            } : { type: typedErr.name, description: typedErr.message }
          }));
        }
      }),
      CHECK_SESSION_DEBOUNCE_TIMER,
      true
    );
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSessionValidity();
      }
    };
    if (!isEmbedded) {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("click", checkSessionValidity);
      document.addEventListener("keydown", checkSessionValidity);
    }
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("click", checkSessionValidity);
      document.removeEventListener("keydown", checkSessionValidity);
    };
  }, [isEmbedded, state.isAuthenticated]);
  useEffect(() => {
    if (isEmbedded) {
      return;
    }
    checkAuth().then(
      ({ isAuthenticated, error: authError, appState: returnedAppState }) => {
        if (!isAuthenticated && !authError && redirectToLogInWhenSignedOut) {
          const { pathname, search } = document.location;
          StandaloneAuth2.client.loginWithRedirect({
            appState: { redirectPath: pathname + search }
          });
        }
        setState({
          isAuthenticated,
          error: authError || null,
          appState: returnedAppState
        });
      }
    );
  }, [redirectToLogInWhenSignedOut, isEmbedded]);
  return state;
}

// src/util/handle-authentication-for-external-links.util.ts
import { StandaloneAuth as StandaloneAuth3 } from "@leagueplatform/auth-standalone";
import { attachCookiesWithAccessToken as attachCookiesWithAccessToken2 } from "@leagueplatform/auth";
import { getConfig as getConfig2 } from "@leagueplatform/config";
function handleAuthenticationForExternalLinks() {
  return __async(this, null, function* () {
    if (StandaloneAuth3.initialized) {
      const { legacyRestApi } = getConfig2().config.core.api;
      if (!legacyRestApi) {
        throw new Error(
          "config.api.legacyRestApi must be specified when calling handleAuthenticationForExternalLinks"
        );
      }
      yield attachCookiesWithAccessToken2(
        legacyRestApi,
        yield StandaloneAuth3.client.getTokenSilently()
      );
    }
  });
}

// src/hooks/use-get-is-impersonator/use-get-is-impersonator.hook.ts
import { useEffect as useEffect2, useState as useState2 } from "react";
import { Auth as Auth2, Implementation as Implementation2 } from "@leagueplatform/auth";
import { StandaloneAuth as StandaloneAuth4 } from "@leagueplatform/auth-standalone";
var getIsImpersonator = () => __async(void 0, null, function* () {
  var _a;
  if (Auth2.initialized && Auth2.implementation === Implementation2.STANDALONE) {
    const claims = yield StandaloneAuth4.client.getIdTokenClaims();
    const isImpersonator = (_a = claims == null ? void 0 : claims["https://el/act_as"]) == null ? void 0 : _a.sub;
    return !!isImpersonator;
  }
  return null;
});
var useGetIsImpersonator = () => {
  const [isImpersonator, setIsImpersonator] = useState2(null);
  useEffect2(() => {
    const checkImpersonation = () => __async(void 0, null, function* () {
      const result = yield getIsImpersonator();
      setIsImpersonator(result);
    });
    checkImpersonation();
  });
  return isImpersonator;
};
export {
  getIsImpersonator,
  handleAuthenticationForExternalLinks,
  useAuth,
  useGetIsImpersonator
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/auth-utils'] = window.leagueSDKsPresent['@leagueplatform/auth-utils'] || [];
    window.leagueSDKsPresent['@leagueplatform/auth-utils'].push('2.23.5');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/auth-utils'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/auth-utils module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map